// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { Component } from 'preact';
import type { ComponentChildren } from 'preact';
import { ErrorScreen } from './error-screen.js';

interface ErrorBoundaryProps {
  children: ComponentChildren;
}

interface ErrorBoundaryState {
  caught: Error | null;
}

/**
 * F17.1 — global error boundary. Catches render-time errors anywhere
 * in the tree below it and shows a managed recovery screen instead
 * of leaving the user staring at a blank page or a half-rendered
 * surface. "Try again" resets the boundary so the user can re-attempt
 * the action that crashed without a hard reload; "Go home" hard-
 * navigates to / for the case where the broken state has bled into
 * router-level data.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { caught: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { caught: error };
  }

  componentDidCatch(error: Error): void {
    // Surface the error in the console so the user / on-call can
    // grab the stack trace from devtools. We deliberately do not
    // ship to a remote sink yet — that's a separate hardening task.
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error);
  }

  private handleRetry = (): void => {
    this.setState({ caught: null });
  };

  private handleGoHome = (): void => {
    if (typeof window !== 'undefined') {
      window.location.assign('/');
    }
  };

  render() {
    if (this.state.caught) {
      return (
        <ErrorScreen
          title="Something went wrong"
          body={
            <p>
              The page hit an unexpected error. Try again, or head back
              to the home screen if it keeps happening.
            </p>
          }
          primaryLabel="Try again"
          onPrimary={this.handleRetry}
          secondaryLabel="Back to home"
          onSecondary={this.handleGoHome}
        />
      );
    }
    return this.props.children;
  }
}
